import { useSyncExternalStore } from 'react'

export type TimeFormat = '24-hour' | '12-hour'

const STORAGE_KEY = 'muster.timeFormat'
const listeners = new Set<() => void>()

function readStored(): TimeFormat | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === '24-hour' || value === '12-hour' ? value : null
  } catch {
    // Storage can be unavailable. 24-hour, this pilot's own data shape,
    // still applies, so there is nothing to report.
    return null
  }
}

let currentFormat: TimeFormat = readStored() ?? '24-hour'

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): TimeFormat {
  return currentFormat
}

// Exported as a plain function, not only through the hook below, so
// non-component code (test cleanup, mainly) can set it imperatively the
// same way scheduleDecisionStore.ts's saveScheduleDecision is called
// directly.
export function setTimeFormat(next: TimeFormat): void {
  currentFormat = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // A preference that cannot be remembered still applies for this visit.
  }
  for (const listener of listeners) listener()
}

/**
 * 24-hour ("0700–1500") is the default and the shape every fixture's own
 * label is already written in. A module-level store notified through
 * useSyncExternalStore, rather than React Context, so the account menu
 * (in AppHeader, mounted separately on every page) and whatever page is
 * currently showing schedule text stay in sync immediately, the same way
 * useTheme's `data-theme` attribute reaches every page without a
 * provider; here the change has to re-render actual text, not just let a
 * CSS variable cascade, hence the subscription instead of a DOM attribute.
 */
export function useTimeFormat(): { format: TimeFormat; setFormat: (next: TimeFormat) => void } {
  const format = useSyncExternalStore(subscribe, getSnapshot)
  return { format, setFormat: setTimeFormat }
}

const SHIFT_TIME_PATTERN = /(\d{2})(\d{2})–(\d{2})(\d{2})/g

function to12Hour(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12
  return `${String(twelveHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`
}

/**
 * A "HHMM–HHMM" shift time appears either leading a work or training label
 * ("0700–1500", "0800–1600 · Academy") or inside a coverage-gap title
 * ("Sep 18 · 0600–1400 · Team 3"), so this searches for the pattern
 * anywhere rather than anchoring to the start; text with no time at all (a
 * day off, a leave type, "Task Force") has nothing to match and comes back
 * unchanged. Reformatting the string here, rather than storing two
 * versions of every label or parsing it into a real Date, means every
 * consumer, fixture data, a board override, an applied code, and an
 * applied leave decision alike, keeps reading and writing the one
 * 24-hour shape; this is the only place that ever converts it for
 * display.
 */
export function formatScheduleLabel(label: string, format: TimeFormat): string {
  if (format === '24-hour') return label
  return label.replace(
    SHIFT_TIME_PATTERN,
    (_wholeMatch, startHour: string, startMinute: string, endHour: string, endMinute: string) =>
      `${to12Hour(Number(startHour), Number(startMinute))} – ${to12Hour(Number(endHour), Number(endMinute))}`,
  )
}

/**
 * A clock time as "HH:MM" (or "HH:MM:SS", as the database holds one) in the
 * chosen format: unchanged in 24-hour, "07:00 AM" in 12-hour. For a time that
 * stands on its own, where formatScheduleLabel's "HHMM–HHMM" shape does not
 * apply.
 */
export function formatClock(time: string, format: TimeFormat): string {
  const [hour, minute] = time.split(':').map(Number)
  if (format === '24-hour' || Number.isNaN(hour) || Number.isNaN(minute)) return time.slice(0, 5)
  return to12Hour(hour, minute)
}

const CLOCK_IN_TEXT = /\b([01]\d|2[0-3]):([0-5]\d)\b/g

/**
 * Every "HH:MM" time in a sentence, in the chosen format, so a description
 * built once ("22:00 to 06:00, by an administrator") follows the preference
 * when it is shown. A date such as 2026-10-01 has no colon and is left alone.
 */
export function formatClockTimesInText(text: string, format: TimeFormat): string {
  if (format === '24-hour') return text
  return text.replace(CLOCK_IN_TEXT, (match) => formatClock(match, format))
}

/**
 * Every time in a description, in either shape this app writes one: "07:00" and
 * "0700–1500". Clock times go first, so the "07:00 AM" they become is not read as
 * a time again by the shift-label pattern, which needs four digits in a row.
 */
export function formatTimesInText(text: string, format: TimeFormat): string {
  return formatScheduleLabel(formatClockTimesInText(text, format), format)
}
